import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Trash2, Users, ClipboardList, Loader2, Shield, Mail } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import TopBar from "@/components/TopBar";

export default function Admin() {
  const [allow, setAllow] = useState([]);
  const [emails, setEmails] = useState("");
  const [role, setRole] = useState("respondent");
  const [busy, setBusy] = useState(false);

  const loadAllow = async () => {
    try { const { data } = await api.get("/admin/allowlist"); setAllow(data); } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => { loadAllow(); }, []);

  const addEmails = async () => {
    const list = emails.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/allowlist", { emails: list, role });
      toast.success(`${data.count} email(s) authorized.`);
      setEmails("");
      loadAllow();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const remove = async (email) => {
    try { await api.delete(`/admin/allowlist/${encodeURIComponent(email)}`); toast.success("Removed."); loadAllow(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="min-h-screen pb-20">
      <TopBar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: "var(--burgundy)" }}>Admin console</h1>
            <p className="text-neutral-500 mt-1 text-sm">Manage who can access the questionnaire.</p>
          </div>
          <Link to="/responses" data-testid="admin-view-responses-link" className="btn btn-outline">
            <ClipboardList className="w-4 h-4" /> View responses
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 mt-6">
          <div className="card-surface overflow-hidden">
            <div className="bg-[#f6f1e6] px-4 py-3 flex items-center gap-2 text-sm font-semibold text-neutral-700">
              <Users className="w-4 h-4" /> Authorized users
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-neutral-600">
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody data-testid="allowlist-table">
                {allow.map((r) => (
                  <tr key={r.email} className="border-t border-[hsl(var(--border))]">
                    <td className="px-4 py-3 flex items-center gap-2"><Mail className="w-4 h-4 text-neutral-400" />{r.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${r.role === "admin" ? "bg-[#f3e8ec] text-[var(--burgundy)]" : "bg-neutral-100 text-neutral-600"}`}>
                        {r.role === "admin" && <Shield className="w-3 h-3" />}{r.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`remove-${r.email}`} onClick={() => remove(r.email)} className="text-neutral-400 hover:text-red-600 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!allow.length && <tr><td colSpan={3} className="px-4 py-8 text-center text-neutral-400">No entries yet.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="card-surface p-5 self-start">
            <h3 className="font-semibold flex items-center gap-2" style={{ color: "var(--burgundy)" }}><UserPlus className="w-4 h-4" /> Authorize access</h3>
            <p className="text-xs text-neutral-500 mt-1 mb-3">Paste one or more emails (comma, space or newline separated).</p>
            <textarea data-testid="allowlist-emails-input" rows={4} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="analyst1@wemabank.com, analyst2@wemabank.com" className="field-input" />
            <div className="flex items-center gap-2 mt-3">
              <select data-testid="allowlist-role-select" value={role} onChange={(e) => setRole(e.target.value)} className="field-input !w-auto">
                <option value="respondent">Respondent</option>
                <option value="operator">Operator (demo view-only)</option>
                <option value="admin">Admin</option>
              </select>
              <button data-testid="allowlist-add-btn" onClick={addEmails} disabled={busy} className="btn btn-primary flex-1">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
